<template>
  <div>
    <div v-if="images.length" class="bg-white rounded-lg overflow-hidden mb-6">
      <div
        class="relative w-full aspect-[16/10] overflow-hidden flex items-center justify-center"
        :class="[images[currentImageIndex].type === 'zumen' ? 'bg-white' : 'bg-black']"
        @click="showLightbox = true"
      >
        <img
          :src="images[currentImageIndex].url"
          class="absolute inset-0 w-full h-full object-cover blur-2xl md:blur-3xl scale-110 saturate-125"
          fetchpriority="high"
          aria-hidden
          alt="Photo background"
          v-if="images[currentImageIndex].type !== 'zumen'"
        />
        <img
          :src="images[currentImageIndex].url"
          class="relative max-w-full max-h-full object-contain"
          fetchpriority="high"
        />

        <!-- Image Navigation -->
        <button
          v-if="images.length > 1"
          class="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2
          rounded-full shadow-lg cursor-pointer"
          aria-label="Show previous image"
          @click.stop="prevImage"
        >
          <ChevronLeft :size="24" class="text-gray-900" />
        </button>
        <button
          v-if="images.length > 1"
          class="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2
          rounded-full shadow-lg cursor-pointer"
          aria-label="Show next image"
          @click.stop="nextImage"
        >
          <ChevronRight :size="24" class="text-gray-900" />
        </button>

        <!-- Image Counter -->
        <div class="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
          {{ currentImageIndex + 1 }} / {{ images.length }}
        </div>
      </div>

      <!-- Thumbnail Strip -->
      <div class="flex gap-2 p-4 overflow-x-auto">
        <button
          v-for="(image, index) in images"
          :key="index"
          :class="`flex-shrink-0 w-20 h-16 rounded overflow-hidden border-2 ${
            index === currentImageIndex
              ? 'border-brand'
              : 'border-transparent'
          }`"
          @click="setCurrentImageIndex(index)"
        >
          <ImageWithFallback
            :src="image.url"
            :alt="`${t('property.thumbnail')} ${index + 1}`"
            class="w-full h-full object-cover"
            loading="lazy"
            @load="onImageLoad"
          />
        </button>
      </div>
    </div>

    <!-- Lightbox -->
    <div v-if="showLightbox" class="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
      <button
        class="absolute top-4 right-4 text-white hover:text-gray-300"
        @click="showLightbox = false"
      >
        <X :size="32" />
      </button>

      <button
        v-if="images.length > 1"
        class="absolute left-4 text-white hover:text-gray-300"
        @click="prevImage"
      >
        <ChevronLeft :size="48" />
      </button>

      <button
        v-if="images.length > 1"
        class="absolute right-4 text-white hover:text-gray-300"
        @click="nextImage"
      >
        <ChevronRight :size="48" />
      </button>

      <div class="max-w-6xl max-h-[90vh] relative">
        <ImageWithFallback
          :src="images[currentImageIndex].url"
          :alt="`${dealName} - ${t('property.image')} ${currentImageIndex + 1}`"
          class="max-w-full max-h-[90vh] object-contain"
        />
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full">
          {{ currentImageIndex + 1 }} / {{ images.length }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronLeft, ChevronRight, X } from 'lucide-vue-next';
import ImageWithFallback from '@repo/ui/components/ImageWithFallback.vue';

interface Image {
  type: string;
  url: string;
}

const props = defineProps<{
  images: Image[];
  dealName: string;
  onImageLoad: (event: Event) => void;
}>();

const { t } = useI18n();

const currentImageIndex = ref(0);
const showLightbox = ref(false);

const setCurrentImageIndex = (index: number) => {
  currentImageIndex.value = index;
};

const nextImage = () => {
  if (props.images.length === 0) return;
  currentImageIndex.value = (currentImageIndex.value + 1) % props.images.length;
};

const prevImage = () => {
  if (props.images.length === 0) return;
  currentImageIndex.value = currentImageIndex.value === 0 ? props.images.length - 1 : currentImageIndex.value - 1;
};

// Reset index if images change
watch(() => props.images, () => {
  currentImageIndex.value = 0;
});
</script>
